import React from 'react';

import {useIntl} from 'react-intl';
import styled from 'styled-components';

import {dimensions} from '@stryd/react-ui';

import {usePageRegionData} from 'src/contexts';
import {VariantPricing} from 'src/features/pricing/helpers';
import {MoneyBag} from 'src/features/pricing/types';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

interface CommitmentSelector {
  id: string;
  name: string;
  description: string[];
  price: string;
  priceHelper: string;
  isSelected: boolean;
  accent: string;
  onClick: () => void;
}

interface CommitmentSelectorProps {
  commitments: CommitmentSelector[];
  productPrices: {
    [key: string]: {
      [key: string]: VariantPricing;
    };
  };
  selectedProduct: string;
  selectedCommitment: string;
  className?: string;
}

const SelectorContainer = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 0 24px 24px 24px;
  box-sizing: border-box;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    width: 100%;
    padding: 0 30px 30px 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const SelectorTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1a1a1a;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    font-size: 24px;
  }
`;

const SelectorDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const ProductOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  max-width: 100%;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    overflow-x: auto;
    padding-bottom: 8px; /* Add some padding to account for scrollbar */
  }
`;

const ProductOption = styled.button<{isSelected: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  border: 2px solid ${(props) => (props.isSelected ? '#41a4ff' : '#e0e0e0')};
  border-radius: 12px;
  background: ${(props) => (props.isSelected ? '#f0f8ff' : '#fff')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    flex: 0 0 280px;
    min-width: 280px;
  }

  &:hover {
    border-color: ${(props) => (props.isSelected ? '#41a4ff' : '#b0b0b0')};
    background: ${(props) => (props.isSelected ? '#f0f8ff' : '#f8f8f8')};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(65, 164, 255, 0.2);
  }
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const ProductDescription = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`;

const CheckmarkIcon = styled.span`
  color: #41a4ff;
  margin-right: 8px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 1px;
`;

const ProductPrice = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #41a4ff;
`;

const ProductPriceHelper = styled.span`
  font-size: 16px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const AccentBadge = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #41a4ff;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;
  white-space: nowrap;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    margin-bottom: 8px;
    display: inline-block;
  }
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #41a4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const YearlyPriceOriginal = styled.span`
  text-decoration: line-through;
`;

const CommitmentSelector: React.FC<
  React.PropsWithChildren<CommitmentSelectorProps>
> = ({
  commitments,
  className,
  productPrices,
  selectedProduct,
  selectedCommitment,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });
  const intl = useIntl();
  const noMembershipPrice =
    productPrices[selectedProduct]['no-membership'].oneTimePricesTotal;
  const monthlyProduct = productPrices[selectedProduct]['monthly-commitment'];
  const monthlyPrice = monthlyProduct.oneTimePricesTotal;
  const yearlyProduct = productPrices[selectedProduct]['yearly-upfront'];

  let monthlySavings = '';
  const priceDifferenceMonthly: MoneyBag = {
    ...noMembershipPrice,
    unit_amount: noMembershipPrice.unit_amount - monthlyPrice.unit_amount,
  };
  monthlySavings = intl.formatMessage(
    {id: 'StoreHero.Duo.saving199'},
    {
      savings: formatOneTimePrice(priceDifferenceMonthly),
    }
  );

  const yearlyPriceOriginal: MoneyBag = {
    ...noMembershipPrice,
    unit_amount:
      Math.trunc(
        (monthlyProduct.recurringPricesTotals[0].unit_amount * 12) / 100
      ) * 100,
  };

  const yearlyPriceDivided: MoneyBag = {
    ...noMembershipPrice,
    unit_amount: yearlyProduct.recurringPricesTotals[0].unit_amount / 12,
  };
  const yearlyPriceOriginalFormatted = intl.formatMessage(
    {id: 'store5.yearlyPriceContext'},
    {
      price: formatOneTimePrice(yearlyPriceDivided),
    }
  );

  const monthlyPriceOriginalFormatted = intl.formatMessage(
    {id: 'store5.monthlyPriceOriginal'},
    {
      price: formatOneTimePrice(monthlyProduct.recurringPricesTotals[0]),
    }
  );

  const yearlyPriceRecurringSavings =
    monthlyProduct.recurringPricesTotals[0].unit_amount * 12 -
    yearlyProduct.recurringPricesTotals[0].unit_amount;
  const yearlyPriceRecurringSavingsRounded =
    Math.trunc(yearlyPriceRecurringSavings / 100) * 100;
  let yearlySavings = '';
  const priceDifferenceYearly: MoneyBag = {
    ...noMembershipPrice,
    unit_amount:
      noMembershipPrice.unit_amount -
      yearlyProduct.oneTimePricesTotal.unit_amount +
      yearlyPriceRecurringSavingsRounded,
  };
  yearlySavings = intl.formatMessage(
    {id: 'StoreHero.Duo.saving199'},
    {
      savings: formatOneTimePrice(priceDifferenceYearly),
    }
  );

  return (
    <SelectorContainer className={className}>
      <SelectorTitle>
        {intl.formatMessage({id: 'store5.membershipTitle'})}
      </SelectorTitle>
      {/* <SelectorDescription>
        Unlock the full features of the Pod with Autopilot. One plan for you and
        your partner. Your Autopilot plan and warranty begin once you activate
        the Pod.
      </SelectorDescription> */}

      <ProductOptions>
        {commitments.map((commitment) => (
          <ProductOption
            key={commitment.id}
            isSelected={commitment.isSelected}
            onClick={commitment.onClick}
            style={{position: 'relative'}}
          >
            {commitment.id == 'monthly-commitment' && (
              <AccentBadge>{monthlySavings}</AccentBadge>
            )}
            {commitment.id == 'yearly-upfront' && (
              <AccentBadge>{yearlySavings}</AccentBadge>
            )}
            {commitment.isSelected && <SelectedIndicator>✓</SelectedIndicator>}
            <ProductName>{commitment.name}</ProductName>
            <ProductDescription>
              <FeatureList>
                {commitment.description.map((feature, index) => (
                  <FeatureItem key={index}>
                    <CheckmarkIcon>✓</CheckmarkIcon>
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>
            </ProductDescription>
            <ProductPrice>
              <span>
                {commitment.price}
                {commitment.id == 'yearly-upfront' && (
                  <span>{yearlyPriceOriginalFormatted}</span>
                )}
              </span>
              <br />
              {commitment.id == 'yearly-upfront' && (
                <YearlyPriceOriginal>
                  {monthlyPriceOriginalFormatted}
                </YearlyPriceOriginal>
              )}
            </ProductPrice>
            <ProductPriceHelper>{commitment.priceHelper}</ProductPriceHelper>
          </ProductOption>
        ))}
      </ProductOptions>
    </SelectorContainer>
  );
};

export default CommitmentSelector;
